/**
 * Hook pour récupérer les sessions et les stocker en cache
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSessionsByDate, updateSession, getLateSessions } from "@/services/sessions.service";
import { format } from "date-fns";
import { WorkSession, LateSession } from "@/types/workSessions.type";
import { toast } from "sonner";

export const useSessions = (date?: Date) => {
  const dateString = date
    ? format(date, "yyyy-MM-dd")
    : format(new Date(), "yyyy-MM-dd");

  return useQuery<WorkSession[]>({
    queryKey: ["sessions", dateString],
    queryFn: () => getSessionsByDate(dateString),
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    networkMode: "offlineFirst",
    retry: (failureCount) => {
      if (failureCount > 0) return false;
      return true;
    },
  });
};

/**
 * Sessions dont l'heure de fin prévue au planning est dépassée
 * et dont l'employé n'a pas encore dépointé.
 */
export const useLateSessions = () => {
  return useQuery<LateSession[]>({
    queryKey: ["late-sessions"],
    queryFn: getLateSessions,
    staleTime: 0,
    retry: false,
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string | null }) =>
      updateSession(id, comment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });

      if (variables.comment && variables.comment.trim()) {
        toast.success("Commentaire ajouté", {
          description: "Le commentaire a été enregistré avec succès.",
        });
      } else {
        toast.success("Commentaire supprimé", {
          description: "Le commentaire a été supprimé avec succès.",
        });
      }
    },
    onError: (error) => {
      toast.error("Erreur", {
        description:
          error instanceof Error
            ? error.message
            : "Une erreur est survenue lors de l'enregistrement du commentaire.",
      });
    },
  });
};
